import { Head, Link, useForm } from "@inertiajs/react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import BackofficeLayout from "@/layouts/backoffice-layout"
import { backofficeUserPath, backofficeUsersPath } from "@/routes"

interface User {
  id: number
  email: string
  name: string | null
  plan: "free" | "plus" | "pro"
  verified: boolean
  google_uid: string | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  daily_operations_count: number
  daily_operations_reset_at: string | null
  documents_count: number
  created_at: string
  updated_at: string
}

interface Props {
  user: User
}

export default function BackofficeUsersShow({ user }: Props) {
  const { data, setData, patch, processing } = useForm({
    plan: user.plan,
    daily_operations_count: user.daily_operations_count,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(backofficeUserPath(user.id))
  }

  const handleResetOperations = () => {
    setData("daily_operations_count", 0)
  }

  return (
    <BackofficeLayout>
      <Head title={`${user.email} - Backoffice`} />

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={backofficeUsersPath()}>Back to users</Link>
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4 rounded-lg border p-4">
            <h2 className="font-semibold">User Information</h2>

            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID</span>
                <span>{user.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span>{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span>{user.name ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Verified</span>
                <Badge variant={user.verified ? "default" : "secondary"}>
                  {user.verified ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Google linked</span>
                <span>{user.google_uid ? "Yes" : "No"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Documents</span>
                <span>{user.documents_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{new Date(user.created_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Updated</span>
                <span>{new Date(user.updated_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border p-4">
            <h2 className="font-semibold">Billing</h2>

            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stripe Customer</span>
                <span className="font-mono text-xs">
                  {user.stripe_customer_id ?? "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subscription</span>
                <span className="font-mono text-xs">
                  {user.stripe_subscription_id ?? "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Operations Reset</span>
                <span>
                  {user.daily_operations_reset_at
                    ? new Date(user.daily_operations_reset_at).toLocaleString()
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-lg border p-4"
        >
          <h2 className="font-semibold">Edit User</h2>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="plan">Plan</Label>
              <Select
                value={data.plan}
                onValueChange={(value) =>
                  setData("plan", value as "free" | "plus" | "pro")
                }
              >
                <SelectTrigger id="plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="plus">Plus</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="operations">Daily Operations Count</Label>
              <div className="flex gap-2">
                <Input
                  id="operations"
                  type="number"
                  min={0}
                  value={data.daily_operations_count}
                  onChange={(e) =>
                    setData(
                      "daily_operations_count",
                      parseInt(e.target.value) || 0,
                    )
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetOperations}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={processing}>
            {processing ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </div>
    </BackofficeLayout>
  )
}
